import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ImageUpload from '../components/ImageUpload';
import RichTextEditor from '../components/RichTextEditor';
import {
  ArrowLeft, Save, Eye, Sparkles, Loader2, Globe, Image as ImageIcon
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostEditor = () => {
  const { user, loading: authLoading, getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = searchParams.get('id');
  const initialType = searchParams.get('type');

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingOg, setGeneratingOg] = useState(false);
  const [loading, setLoading] = useState(!!postId);

  const [post, setPost] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    category: initialType === 'press-release' ? 'press-release' : 'news',
    post_type: initialType || 'article',
    status: 'draft',
    featured_image: '',
    og_image: '',
    seo_title: '',
    seo_description: '',
    external_url: '',
    published_at: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (postId && user) {
      const token = getToken();
      axios.get(`${API_URL}/api/admin/post?id=${postId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      .then(res => { setPost(res.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load post'); setLoading(false); });
    }
  }, [postId, user, getToken]);

  const updateField = (field, value) => {
    setPost(prev => ({ ...prev, [field]: value }));
  };

  const autoSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80);
  };

  const handleTitleChange = (value) => {
    updateField('title', value);
    if (!postId || !post.slug) {
      updateField('slug', autoSlug(value));
    }
  };

  const savePost = async (status) => {
    if (!post.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const token = getToken();
      const headers = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
      const payload = { ...post, status: status || post.status };
      if (!payload.slug) payload.slug = autoSlug(payload.title);

      if (postId) {
        await axios.put(`${API_URL}/api/admin/post?id=${postId}`, payload, headers);
        toast.success('Post saved');
      } else {
        const res = await axios.post(`${API_URL}/api/admin/posts`, payload, headers);
        toast.success('Post created');
        navigate(`/admin/post?id=${res.data.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const generateSeo = async () => {
    if (!post.title && !post.content) {
      toast.error('Add a title and some content first');
      return;
    }
    setGenerating(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API_URL}/api/admin/generate-seo`, {
        title: post.title,
        content: post.content,
        excerpt: post.excerpt
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const seo = res.data;
      setPost(prev => ({
        ...prev,
        seo_title: seo.seo_title || prev.seo_title,
        seo_description: seo.seo_description || prev.seo_description,
        excerpt: seo.excerpt || prev.excerpt,
      }));
      toast.success('SEO metadata generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SEO generation failed — check OpenAI API key');
    } finally {
      setGenerating(false);
    }
  };

  const generateOgImage = async () => {
    if (!post.title) {
      toast.error('Add a title first');
      return;
    }
    setGeneratingOg(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API_URL}/api/admin/generate-og-image`, {
        title: post.title,
        featured_image: post.featured_image
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const url = res.data.url.startsWith('/') ? `${API_URL}${res.data.url}` : res.data.url;
      updateField('og_image', url);
      toast.success('Share image generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image generation failed');
    } finally {
      setGeneratingOg(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f5]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: '#9B1E37', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#f5f5f5]" data-testid="post-editor">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="sticky top-0 z-50 shadow-sm" style={{ backgroundColor: '#9B1E37' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-white hover:bg-white/10">
              <ArrowLeft size={16} className="mr-2" />
              Back
            </Button>
            <h1 className="text-lg font-bold text-white">{postId ? 'Edit Post' : 'New Post'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => savePost('draft')} disabled={saving} className="text-white hover:bg-white/10">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Save Draft
            </Button>
            <Button size="sm" onClick={() => savePost('published')} disabled={saving} className="bg-white text-[#9B1E37] hover:bg-white/90">
              <Globe size={14} className="mr-2" />
              Publish
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content — Left */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={post.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Enter post title..."
                    className="text-lg font-semibold"
                    data-testid="post-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input
                    value={post.slug}
                    onChange={(e) => updateField('slug', e.target.value)}
                    placeholder="post-url-slug"
                    className="text-sm text-slate-600"
                    data-testid="post-slug-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Excerpt</Label>
                  <Textarea
                    value={post.excerpt}
                    onChange={(e) => updateField('excerpt', e.target.value)}
                    placeholder="Brief summary of the post..."
                    rows={2}
                    data-testid="post-excerpt-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Rich Text Editor */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Content</CardTitle>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  content={post.content}
                  onChange={(html) => updateField('content', html)}
                  getToken={getToken}
                  placeholder="Start writing your post..."
                />
              </CardContent>
            </Card>

            {/* SEO Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">SEO & Social</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateSeo}
                  disabled={generating}
                  data-testid="generate-seo-btn"
                >
                  {generating ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
                  Auto-Generate SEO
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>SEO Title</Label>
                  <Input
                    value={post.seo_title}
                    onChange={(e) => updateField('seo_title', e.target.value)}
                    placeholder="Page title for search engines (max 60 chars)"
                    maxLength={60}
                  />
                  <span className="text-xs text-slate-400">{(post.seo_title || '').length}/60</span>
                </div>
                <div className="space-y-2">
                  <Label>SEO Description</Label>
                  <Textarea
                    value={post.seo_description}
                    onChange={(e) => updateField('seo_description', e.target.value)}
                    placeholder="Meta description for search results (max 160 chars)"
                    maxLength={160}
                    rows={2}
                  />
                  <span className="text-xs text-slate-400">{(post.seo_description || '').length}/160</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar — Right */}
          <div className="space-y-6">
            {/* Post Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <select
                    value={post.category}
                    onChange={(e) => updateField('category', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    data-testid="post-category"
                  >
                    <option value="news">News / General</option>
                    <option value="press-release">Press Release / General</option>
                    <option value="in-the-news">In the News</option>
                    <option value="citizen-action">Citizen Action</option>
                    <option value="lawsuit-document">Lawsuit Document</option>
                    <option value="spotlight-series">Spotlight Series</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Post Type</Label>
                  <select
                    value={post.post_type}
                    onChange={(e) => updateField('post_type', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    data-testid="post-type"
                  >
                    <option value="article">Article / Blog</option>
                    <option value="page">Static Page</option>
                    <option value="press-release">Press Release</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select
                    value={post.status}
                    onChange={(e) => updateField('status', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Published Date</Label>
                  <Input
                    type="date"
                    value={post.published_at ? post.published_at.split('T')[0] : new Date().toISOString().split('T')[0]}
                    onChange={(e) => updateField('published_at', e.target.value)}
                    className="text-sm"
                    data-testid="post-published-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>External URL (optional)</Label>
                  <Input
                    value={post.external_url || ''}
                    onChange={(e) => updateField('external_url', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Featured Image */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Featured Image</CardTitle>
              </CardHeader>
              <CardContent>
                <ImageUpload
                  value={post.featured_image}
                  onChange={(url) => updateField('featured_image', url)}
                  getToken={getToken}
                />
              </CardContent>
            </Card>

            {/* Hero / OG Image */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Share / Hero Image</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateOgImage}
                  disabled={generatingOg}
                  data-testid="generate-og-btn"
                >
                  {generatingOg ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                </Button>
              </CardHeader>
              <CardContent>
                <ImageUpload
                  value={post.og_image}
                  onChange={(url) => updateField('og_image', url)}
                  getToken={getToken}
                />
                <p className="text-xs text-slate-400 mt-2">Used for social sharing and page hero background. Click the button above to auto-generate from title + featured image.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PostEditor;
